import { ApiProjectType, ApiReservedConversion } from "@incendium/api";

export interface GroupedReservedConversions {
  group: string;
  conversions: ApiReservedConversion[];
}

/**
 * Filters reserved conversions based on project type and returns them grouped by category
 * @param projectType - The type of project
 * @returns Array of grouped reserved conversions for the project type
 */
export const getRelevantReservedConversions = (
  projectType?: ApiProjectType,
): GroupedReservedConversions[] => {
  // Generic conversions available for all project types
  const genericConversions: ApiReservedConversion[] = [
    ApiReservedConversion.EXTERNAL_LINK,
    ApiReservedConversion.CLICK_TO,
    ApiReservedConversion.COMPLETE_REGISTRATION,
    ApiReservedConversion.START_TRIAL,
    ApiReservedConversion.VIDEO_PLAY,
    ApiReservedConversion.SEARCH,
    ApiReservedConversion.CTA_CLICK,
    ApiReservedConversion.ENQUIRY_SUBMITTED,
    ApiReservedConversion.WISHLIST_ADDED,
  ];

  // Ecommerce-specific conversions
  const ecommerceConversions: ApiReservedConversion[] = [
    ApiReservedConversion.ADD_TO_BASKET,
    ApiReservedConversion.CHECKOUT,
    ApiReservedConversion.CART_VIEWED,
    ApiReservedConversion.SALE,
    ApiReservedConversion.PAYMENT_INFO_SUBMITTED,
    ApiReservedConversion.SHIPPING_INFO_SUBMITTED,
  ];

  // Recruitment-specific conversions
  const recruitmentConversions: ApiReservedConversion[] = [
    ApiReservedConversion.JOB_APPLICATION,
    ApiReservedConversion.JOB_APPLY_CLICK,
  ];

  // If no project type or unspecified, show all conversions grouped
  if (!projectType || projectType === ApiProjectType.TYPE_UNSPECIFIED) {
    return [
      {
        group: "Generic",
        conversions: genericConversions,
      },
      {
        group: "Ecommerce",
        conversions: ecommerceConversions,
      },
      {
        group: "Recruitment",
        conversions: recruitmentConversions,
      },
    ];
  }

  // Return relevant conversions based on project type
  switch (projectType) {
    case ApiProjectType.TYPE_ECOMMERCE:
      return [
        {
          group: "Generic",
          conversions: genericConversions,
        },
        {
          group: "Ecommerce",
          conversions: ecommerceConversions,
        },
      ];

    case ApiProjectType.TYPE_RECRUITMENT:
      return [
        {
          group: "Generic",
          conversions: genericConversions,
        },
        {
          group: "Recruitment",
          conversions: recruitmentConversions,
        },
      ];

    case ApiProjectType.TYPE_B2_B:
      // B2B projects get generic conversions
      return [
        {
          group: "Generic",
          conversions: genericConversions,
        },
      ];

    default:
      // Default to showing all conversions
      return [
        {
          group: "Generic",
          conversions: genericConversions,
        },
        {
          group: "Ecommerce",
          conversions: ecommerceConversions,
        },
        {
          group: "Recruitment",
          conversions: recruitmentConversions,
        },
      ];
  }
};
